import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHall } from '$logic/hall/react-hook'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { useAsyncFunction } from '$helper/functions/async-function.react-hook'
import { hasPremiumPlanTag, waitForPackagesStatsToSync } from './service'
import { packagesStatsView } from './storage'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { IS_IN_ALPHA_SCHOOL } from '$setup/config/env'

export function usePackagesStats() {
  return useSelector(packagesStatsView)
}

export function useIsPremiumPlanEnabled(hallId: string) {
  if (IS_IN_ALPHA_SCHOOL) return { value: true, loading: false }

  const { entity: hall, loading: loadingHall } = useHall(hallId)

  const permissions = useClientUserPermissions(hallId)

  const isAdmin = permissions?.isAdmin

  const isPremium = useMemo(() => {
    return hasPremiumPlanTag(hall?.tags)
  }, [hall?.tags])

  const packagesStats = usePackagesStats()

  const isTrialEnabled = useMemo(() => {
    if (!packagesStats || !packagesStats.trialExpiresAt) return undefined
    return packagesStats.trialExpiresAt > dateWithOffset()
  }, [packagesStats])

  const { loading: packagesStatsLoading } = useAsyncFunction(async () => {
    await waitForPackagesStatsToSync()
  }, [])

  return {
    value: (isPremium || isTrialEnabled) && isAdmin,
    loading: loadingHall || (!isPremium && packagesStatsLoading),
  }
}

export function useOngoingTrialExpirationDate() {
  const packagesStats = usePackagesStats()
  const { loading } = useAsyncFunction(waitForPackagesStatsToSync, [])

  const expiresAt = useMemo(() => {
    if (!packagesStats || !packagesStats.trialExpiresAt) return undefined

    return packagesStats.trialExpiresAt > dateWithOffset()
      ? packagesStats.trialExpiresAt
      : undefined
  }, [packagesStats])

  return { expiresAt, loading }
}

export function useIsAnyPlanEnabled() {
  const packagesStats = usePackagesStats()
  return !!packagesStats?.hasAnyPackages
}

export function useIsVIPPlanEnabledForUser() {
  if (IS_IN_ALPHA_SCHOOL) return true
  // after implmenatation in dashboard change this logic and fetch from server
  return useIsAnyPlanEnabled()
}
